/**
 * Phase 13b — Autopilot evidence chips.
 *
 * Fetches `/autopilot/actions/{id}/evidence` on mount and renders a
 * "Based on" chip row with up to 3 memory-entry pills + "+N more" when
 * an action was memory-grounded. Renders nothing when the endpoint
 * returns zero evidence — the caller doesn't have to gate on it.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { BrainCircuit } from "lucide-react";

const COPY = {
  de: { based_on: "Basiert auf", more: (n) => `+${n} weitere` },
  en: { based_on: "Based on", more: (n) => `+${n} more` },
};

function truncate(s, n = 40) {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export default function AutopilotEvidenceChips({
  actionId, locale = "de", onOpenEntry,
}) {
  const t = COPY[locale] || COPY.en;
  const [evidence, setEvidence] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/autopilot/actions/${actionId}/evidence`)
      .then((r) => { if (alive) setEvidence(r.data?.evidence || []); })
      .catch(() => { if (alive) setEvidence([]); });
    return () => { alive = false; };
  }, [actionId]);

  if (!evidence || evidence.length === 0) return null;
  const visible = evidence.slice(0, 3);
  const remainder = evidence.length - visible.length;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`autopilot-evidence-${actionId}`}
    >
      <span className="text-[10px] uppercase text-muted-foreground inline-flex items-center gap-1">
        <BrainCircuit className="size-3" />
        {t.based_on}
      </span>
      {visible.map((e, i) => (
        <button
          type="button"
          key={e.entry_id}
          data-testid={`autopilot-evidence-${actionId}-chip-${i}`}
          data-entry-id={e.entry_id}
          onClick={() => onOpenEntry && onOpenEntry(e.entry_id)}
          title={`${e.claim} — conf ${Number(e.confidence || 0).toFixed(2)}`}
          className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-900 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-400"
        >
          <span className="font-mono text-[9px] uppercase opacity-60">
            {e.dimension || "?"}
          </span>
          <span>{truncate(e.claim)}</span>
        </button>
      ))}
      {remainder > 0 && (
        <span
          data-testid={`autopilot-evidence-${actionId}-more`}
          className="inline-flex items-center rounded-full border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
        >{t.more(remainder)}</span>
      )}
    </div>
  );
}
